/**
 * Adding a step (ADR-0071 phase 3, board 14): click + on an arrow (or "Add a step"), say what the
 * step should do in plain words, answer three questions, and the step is wired in where the arrow
 * was. Every chooser kind maps onto the spec's node types; nothing new in the spec.
 */
import type { JsonSchemaLite, Text } from "../types";
import { answerKey } from "./datamap";
import { decisionOptions, defaultNode, editorEdges, ensureGrant, ensureKnowledge, routesByOption, uniqueId } from "./editor";
import type { CalcValue } from "./formula";
import { idFromLabel, template } from "./plain";
import type { AgentSpec, SpecEdge, SpecNode } from "./types";

export type AddKind = "ai" | "code" | "calc" | "web" | "system" | "knowledge" | "table" | "agent" | "approve" | "ask" | "handover" | "choose" | "parallel" | "foreach" | "await" | "end";
export type AddGroup = "think" | "info" | "person" | "flow";

/** The chooser, in its groups and order (board 14). */
export const STEP_KINDS: readonly { kind: AddKind; group: AddGroup }[] = [
  { kind: "ai", group: "think" },
  { kind: "code", group: "think" },
  { kind: "calc", group: "think" },
  { kind: "system", group: "info" },
  { kind: "web", group: "info" },
  { kind: "knowledge", group: "info" },
  { kind: "table", group: "info" },
  { kind: "agent", group: "info" },
  { kind: "approve", group: "person" },
  { kind: "ask", group: "person" },
  { kind: "handover", group: "person" },
  { kind: "choose", group: "flow" },
  { kind: "parallel", group: "flow" },
  { kind: "foreach", group: "flow" },
  { kind: "await", group: "flow" },
  { kind: "end", group: "flow" },
];

/**
 * Where the new step goes: on an arrow (between its two steps), after a step, or on its own.
 * After a choice made by the AI, `when` is the answer the new step is for (G-flow-05).
 */
export type StepPlace = { edgeId: string } | { after: string; when?: string } | null;

/** The kinds of step a new "For each item" step can do for each item (ADR-0104). */
export type EachKind = "ai" | "system" | "ask" | "calc" | "agent";
export const EACH_KINDS: readonly EachKind[] = ["ai", "system", "ask", "calc", "agent"];

/** The kinds a "Several at once" step can make new side-by-side steps of (G-flow-10). */
export type BranchKind = "ai" | "system" | "knowledge" | "agent";
export const BRANCH_KINDS: readonly BranchKind[] = ["ai", "system", "knowledge", "agent"];

/** The answers to the three questions; each kind reads its own. */
export interface StepAnswers {
  label?: Text;
  // ai
  instructions?: string;
  fields?: string[];
  /** What kind of value each field is (fin-03): a number, an amount of money, a date, … (text by default). */
  fieldKinds?: Record<string, FieldKind>;
  /** The possible answers of a "one of a list" field. */
  fieldChoices?: Record<string, string[]>;
  // calc (ADR-0103): the values worked out, in order
  calc?: CalcValue[];
  // foreach (ADR-0104): the list, what is done for each item, and how
  list?: string;
  each?: { mode: "step" | "agent"; kind?: EachKind; label?: Text; agent?: string };
  onItemError?: "skip" | "stop" | "ask";
  // table (ADR-0105): read a file or value, match two tables, or group with totals
  table?: Partial<SpecNode>;
  maxItems?: number;
  concurrency?: number;
  systems?: string[];
  // code
  file?: string;
  fn?: string;
  reads?: string[];
  // web
  url?: string;
  urlFrom?: string;
  many?: boolean;
  script?: string;
  // system
  tool?: string;
  action?: string;
  // knowledge
  collection?: string;
  query?: string;
  // agent
  agent?: string;
  // approve
  subject?: string;
  roles?: string[];
  sla?: string;
  onReject?: string | null;
  // ask
  question?: Text;
  answer?: "text" | "number" | "yes_no" | "choice";
  choices?: string[];
  // system: values the action needs, as typed or picked (G-kn-12)
  args?: Record<string, unknown>;
  // choose
  mode?: "rule" | "ai";
  /** The possible answers: a word (used in both languages) or a name in each. */
  options?: (string | Text)[];
  /** How sure the AI must be (0–1); below it, `unsure` happens. */
  threshold?: number;
  /** When the AI is unsure: ask a person ("ask", the default) or go to a step. */
  unsure?: string;
  // parallel
  branches?: string[];
  /** New steps to run side by side, made with the "Several at once" step. */
  newBranches?: { label: Text; kind: BranchKind }[];
  combine?: "merge" | "list" | "vote";
  /** The name of what the step makes. */
  makes?: Text;
  // await ("Wait for", ADR-0094): the wait step's own fields
  wait?: Partial<SpecNode>;
  // handover ("Hand over to a person", G-camp-cs-02): why, for the desk
  reason?: Text;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** Retargets one arrow (any kind) to another step, keeping its condition. */
export function retargetEdge(spec: AgentSpec, edgeId: string, to: string): AgentSpec {
  const [kind, a, b] = edgeId.split(":") as [string, string, string | undefined];
  const g = spec.spec.graph;
  if (kind === "edge") {
    const i = Number(a);
    return { ...spec, spec: { ...spec.spec, graph: { ...g, edges: (g.edges ?? []).map((e, j) => (j === i ? { ...e, to } : e)) } } };
  }
  const nodes = g.nodes.map((n) => {
    if (n.id !== a) return n;
    const i = b === undefined ? -1 : Number(b);
    if (kind === "route") return { ...n, routes: (n.routes ?? []).map((r, j) => (j === i ? { ...r, to } : r)) };
    if (kind === "default") return { ...n, default: to };
    if (kind === "branch") return { ...n, branches: (n.branches ?? []).map((x, j) => (j === i ? to : x)) };
    if (kind === "reject") return { ...n, on_reject: to };
    if (kind === "low_confidence") return { ...n, on_low_confidence: { route: to } };
    if (kind === "limit") return { ...n, on_limit: to };
    if (kind === "error") return { ...n, on_error: to };
    if (kind === "give_up") return { ...n, on_give_up: to };
    // "For each item": the new first step of what is done for each item (ADR-0104).
    if (kind === "each") return { ...n, steps: [to, ...(n.steps ?? []).filter((s) => s !== to)] };
    return n;
  });
  return { ...spec, spec: { ...spec.spec, graph: { ...g, nodes } } };
}

/** The steps on either side of where the new step goes. */
export function placeEnds(spec: AgentSpec, place: StepPlace): { from: string | null; to: string | null } {
  if (!place) return { from: null, to: null };
  if ("after" in place) {
    // An answer of an AI choice that already has a path: the step goes on that arrow.
    const e = place.when ? editorEdges(spec).find((x) => x.source === place.after && x.when === place.when) : undefined;
    return { from: place.after, to: e?.target ?? null };
  }
  const e = editorEdges(spec).find((x) => x.id === place.edgeId);
  return e ? { from: e.source, to: e.target } : { from: null, to: null };
}

/** Values the platform fills in every prompt itself. */
export const PROMPT_BUILT_INS: readonly string[] = ["attachments", "today", "weekday", "now"];
/** A prompt placeholder: `{name}`, or a value inside it: `{trigger.order_id}`, `{search.clubs[0].name}`. */
export const PROMPT_PLACEHOLDER = /(?<!\{)\{([A-Za-z_]\w*)((?:\.[A-Za-z_]\w*|\[\w+\])*)\}(?!\})/g;

/** Every placeholder in a prompt (`{trigger.order_id}` → root `trigger`, path `trigger.order_id`). */
export function promptPlaceholders(text: string): { root: string; path: string }[] {
  const out: { root: string; path: string }[] = [];
  for (const m of text.matchAll(PROMPT_PLACEHOLDER)) {
    const path = `${m[1] as string}${m[2] ?? ""}`;
    if (!out.some((x) => x.path === path)) out.push({ root: m[1] as string, path });
  }
  return out;
}

/** The values a prompt reads (its `variables`): the root of each placeholder, built-ins left out (G-io-03). */
export function promptVariables(text: string): string[] {
  return [...new Set(promptPlaceholders(text).map((p) => p.root))].filter((v) => !PROMPT_BUILT_INS.includes(v));
}

/**
 * The values a written prompt reads after an edit: the ones still used are kept, and each new
 * placeholder whose value the agent knows is added, also a value known only by its fields
 * (`{trigger.message}` typed by hand with only `trigger.message` offered, G-camp-cs-22).
 */
export function promptValuesUsed(text: string, previous: readonly string[], known: readonly string[]): string[] {
  const used = promptVariables(text);
  const keep = previous.filter((v) => used.includes(v.split(/[.[]/)[0] as string));
  const knows = (u: string) => known.some((k) => k === u || k.startsWith(`${u}.`) || k.startsWith(`${u}[`));
  return [...new Set([...keep, ...used.filter(knows)])];
}

/** The options of a new AI choice: a value from the English (or Arabic) name, both names kept. */
export function choiceOptions(list: readonly (string | Text)[]): { value: string; label: Text }[] {
  const out: { value: string; label: Text }[] = [];
  list.forEach((o, i) => {
    const t: Text = typeof o === "string" ? { en: o, ar: o } : o;
    const en = t.en.trim() || t.ar.trim();
    const ar = t.ar.trim() || t.en.trim();
    if (!en) return;
    let value = idFromLabel(en) || `option_${i + 1}`;
    while (out.some((x) => x.value === value)) value = `${value}_${i + 1}`;
    out.push({ value, label: { en, ar } });
  });
  return out;
}

/** What kind of value an AI field is (fin-03); the gateway checks the answer against it and asks the model to fix it. */
export type FieldKind = "text" | "number" | "money" | "date" | "yes_no" | "choice" | "list";
export const FIELD_KINDS: readonly FieldKind[] = ["text", "number", "money", "date", "yes_no", "choice", "list"];

/** The JSON Schema of one field of a kind. */
export function fieldKindSchema(kind: FieldKind, choices: readonly string[] = []): Record<string, unknown> {
  switch (kind) {
    case "number":
      return { type: "number", "x-kind": "number" };
    case "money":
      return {
        type: "object",
        "x-kind": "money",
        properties: { amount: { type: "number", description: "The amount, a number without the currency" }, currency: { type: "string", pattern: "^[A-Z]{3}$", description: "Three-letter currency code, e.g. SAR, AED, USD" } },
        required: ["amount", "currency"],
        additionalProperties: false,
      };
    case "date":
      return { type: "string", format: "date", pattern: "^\\d{4}-\\d{2}-\\d{2}$", description: "A date as YYYY-MM-DD", "x-kind": "date" };
    case "yes_no":
      return { type: "boolean", "x-kind": "yes_no" };
    case "choice":
      return choices.length ? { type: "string", enum: [...choices], "x-kind": "choice" } : { type: "string", "x-kind": "choice" };
    case "list":
      return { type: "array", items: { type: "string" }, "x-kind": "list" };
    default:
      return { type: "string" };
  }
}

/** The kind a field's schema stands for (text when it is plain text or not one of the kinds). */
export function fieldKindOf(schema: unknown): FieldKind {
  if (!schema || typeof schema !== "object") return "text";
  const s = schema as Record<string, unknown>;
  const k = s["x-kind"];
  if (typeof k === "string" && (FIELD_KINDS as readonly string[]).includes(k)) return k as FieldKind;
  const t = Array.isArray(s.type) ? (s.type as string[]).find((x) => x !== "null") : s.type;
  if (t === "number" || t === "integer") return "number";
  if (t === "boolean") return "yes_no";
  if (t === "array") return "list";
  if (t === "string" && s.format === "date") return "date";
  if (t === "string" && Array.isArray(s.enum)) return "choice";
  return "text";
}

/** The fields a step gives; all required, so the AI can't silently drop one (G-kn-21). */
function schemaFor(fields: string[], kinds: Record<string, FieldKind> = {}, choices: Record<string, string[]> = {}): Record<string, unknown> {
  return { type: "object", properties: Object.fromEntries(fields.map((f) => [f, fieldKindSchema(kinds[f] ?? "text", choices[f] ?? [])])), required: [...fields] };
}

/**
 * What a new AI "Choose a path" reads (G-camp-ops-12): the question, and what the step right
 * before it found (its passages, fields or answer), so a choice placed after "Find the rules"
 * sees the rules without rewriting "What the agent reads".
 */
export function choiceInput(spec: AgentSpec, from: string | null): string {
  const before = from ? spec.spec.graph.nodes.find((n) => n.id === from) : undefined;
  const key = before ? (writtenKey(before) ?? (before.type === "llm" ? answerKey(before.id) : null)) : null;
  return key && key !== "question" ? `{{ state.question }}\n\n{{ state.${key} }}` : "{{ state.question }}";
}

const LEVELS = ["public", "internal", "confidential", "restricted"];

/**
 * A new step that makes a file (a PDF, a spreadsheet) at the agent's data level (G-camp-ops-22):
 * the platform refuses a file below the level of the data the agent handles. Unchanged when the
 * builder set the level, the action has no level, or it can't go that high.
 */
export function withAgentLevel(args: Record<string, unknown>, schema: JsonSchemaLite | undefined, agentLevel: string): Record<string, unknown> {
  const prop = schema?.properties?.data_level as { enum?: unknown[]; default?: unknown } | undefined;
  if (!prop || (args.data_level !== undefined && args.data_level !== "")) return args;
  const allowed = Array.isArray(prop.enum) ? prop.enum.map(String) : LEVELS;
  const base = typeof prop.default === "string" ? prop.default : "internal";
  if (LEVELS.indexOf(agentLevel) <= LEVELS.indexOf(base) || !allowed.includes(agentLevel)) return args;
  return { ...args, data_level: agentLevel };
}

/** The new nodes (the first is where it starts, `exit` where it continues) for a kind and its answers. */
export function buildStep(spec: AgentSpec, kind: AddKind, ans: StepAnswers, to: string | null, from: string | null = null): { nodes: SpecNode[]; edges: SpecEdge[]; entry: string; exit: string | null } {
  const base = idFromLabel(ans.label?.en ?? "") || kind;
  const id = uniqueId(spec, base);
  const label = ans.label && (ans.label.en || ans.label.ar) ? { en: ans.label.en || ans.label.ar, ar: ans.label.ar || ans.label.en } : undefined;
  const makes = ans.makes && (ans.makes.en || ans.makes.ar) ? { output_label: { en: ans.makes.en || ans.makes.ar, ar: ans.makes.ar || ans.makes.en } } : {};
  const common = { id, ...(label ? { label } : {}) };
  const one = (n: SpecNode) => ({ nodes: [n], edges: [] as SpecEdge[], entry: id, exit: n.type === "end" ? null : id });
  switch (kind) {
    case "ai": {
      const text = ans.instructions ?? "";
      const fields = (ans.fields ?? []).filter(Boolean);
      return one({
        ...common,
        type: "llm",
        prompt: { template: text, variables: promptVariables(text) },
        tools: ans.systems ?? [],
        ...(fields.length ? { model: { alias: spec.spec.model.alias, output_schema: schemaFor(fields, ans.fieldKinds, ans.fieldChoices) }, output_key: `${id}_answer` } : {}),
        ...makes,
      });
    }
    case "code": {
      const input: Record<string, unknown> = {};
      if (ans.fn) input.step = ans.fn;
      for (const r of ans.reads ?? []) input[r.split(".").pop() ?? r] = template(r);
      const fileArgs = ans.file ? { code: "import recipe\n\nrecipe.main()\n", files: { [ans.file]: { $file: ans.file } } } : { code: "import json, sys\n\ndata = json.load(open('input.json'))\nprint(json.dumps({'result': data}))\n" };
      const fields = (ans.fields ?? []).filter(Boolean);
      return one({
        ...common,
        type: "tool",
        tool: "codeexec",
        action: "run_python",
        args: { ...fileArgs, input, output: "json", timeout_s: 30 },
        output_field: "result",
        ...(fields.length ? { output_schema: schemaFor(fields, ans.fieldKinds, ans.fieldChoices) } : {}),
        ...makes,
      });
    }
    case "calc": {
      const values = (ans.calc ?? []).filter((c) => c.key && c.expr);
      return one({ ...common, type: "calculate", values: values.length ? values : [{ key: "result", expr: "0" }], ...makes });
    }
    case "web": {
      const pages = ans.urlFrom ? template(ans.urlFrom) : (ans.url ?? "");
      const script = ans.script ? { script: { $file: ans.script } } : {};
      return one({ ...common, type: "tool", tool: "browser", action: ans.many ? "read_pages" : "read_page", args: { [ans.many ? "urls" : "url"]: pages, ...script }, ...makes });
    }
    case "system": {
      const args = Object.fromEntries(Object.entries(ans.args ?? {}).filter(([, v]) => v !== undefined && v !== ""));
      return one({ ...common, type: "tool", tool: ans.tool ?? "", action: ans.action ?? "", args, ...makes });
    }
    case "knowledge":
      return one({ ...common, type: "retrieval", collection: ans.collection ?? "", ...(ans.query ? { query: ans.query } : {}), ...makes });
    case "agent":
      return one({ ...common, type: "subgraph", agent: ans.agent ?? "", ...makes });
    case "approve":
      return one({ ...common, type: "human_approval", subject_key: ans.subject ?? "", approver_roles: ans.roles?.length ? ans.roles : ["approver"], sla: ans.sla ?? "PT4H", on_reject: ans.onReject ?? null });
    case "ask": {
      const schema = ans.answer === "number" ? { type: "number" } : ans.answer === "yes_no" ? { type: "boolean" } : ans.answer === "choice" ? { type: "string", enum: ans.choices ?? [] } : undefined;
      return one({ ...common, type: "human_input", question: ans.question ?? { ar: "", en: "" }, output_key: `${id}_answer`, ...(schema ? { answer_schema: schema } : {}), ...makes });
    }
    case "choose": {
      if (ans.mode === "ai") {
        // Unanswered parts keep the decision step's defaults (two example options), to edit after.
        const options = choiceOptions(ans.options ?? []);
        // The question in both languages (one is copied when only one is written), else a plain default.
        const qa = ans.question ?? { en: "", ar: "" };
        const question = qa.en.trim() || qa.ar.trim() ? { en: qa.en.trim() || qa.ar.trim(), ar: qa.ar.trim() || qa.en.trim() } : { en: label?.en ? `${label.en}?` : "Which way should this go?", ar: label?.ar ? `${label.ar}؟` : "أي طريق يجب أن يسلك؟" };
        const q = { question };
        const node: SpecNode = { ...defaultNode("decision", id), ...common, ...q, input: choiceInput(spec, from), ...(options.length ? { options } : {}) };
        if (typeof ans.threshold === "number") node.threshold = ans.threshold;
        if (ans.unsure && ans.unsure !== "ask") node.on_low_confidence = { route: ans.unsure };
        // One arrow per answer (G-flow-05): each goes on to where the arrow went, to change after.
        const edges = to ? (node.options ?? []).map((o) => ({ from: id, to, when: o.value })) : [];
        return { nodes: [node], edges, entry: id, exit: id };
      }
      // No rules yet: everything takes the default path, which is valid (G-flow-09).
      return one({ ...common, type: "router", routes: [], default: to ?? "" });
    }
    case "table": {
      const fields = Object.fromEntries(Object.entries(ans.table ?? { op: "read" }).filter(([k, v]) => v !== undefined && v !== null && v !== "" && k !== "id" && k !== "type"));
      return one({ ...common, type: "table", op: "read", ...fields, ...makes });
    }
    case "foreach": {
      const node: SpecNode = { ...common, type: "foreach", items: ans.list ?? "", concurrency: ans.concurrency ?? 4, max_items: ans.maxItems ?? 100, on_item_error: ans.onItemError ?? "skip", ...makes };
      if (ans.each?.mode === "agent") return { nodes: [{ ...node, agent: ans.each.agent ?? "" }], edges: [], entry: id, exit: id };
      // The first step done for each item, from its kind's defaults; more can be added on its arrow.
      const taken: AgentSpec = { ...spec, spec: { ...spec.spec, graph: { ...spec.spec.graph, nodes: [...spec.spec.graph.nodes, node] } } };
      const kind = ans.each?.kind ?? "ai";
      const innerLabel: Text = ans.each?.label && (ans.each.label.en || ans.each.label.ar) ? ans.each.label : { en: `${label?.en ?? "Each item"}: do it`, ar: `${label?.ar ?? "كل عنصر"}: نفّذ` };
      const inner = buildStep(taken, kind, { label: innerLabel, ...(kind === "calc" ? { calc: [{ key: "item_result", expr: "state.item" }] } : kind === "ask" ? { question: { en: "What about {{ state.item }}?", ar: "ماذا عن {{ state.item }}؟" } } : kind === "ai" ? { instructions: "Work on this item: {item}" } : {}) }, null).nodes[0] as SpecNode;
      return { nodes: [{ ...node, steps: [inner.id] }, inner], edges: [], entry: id, exit: id };
    }
    case "handover": {
      const r = ans.reason && (ans.reason.en.trim() || ans.reason.ar.trim()) ? { en: ans.reason.en.trim() || ans.reason.ar.trim(), ar: ans.reason.ar.trim() || ans.reason.en.trim() } : null;
      return one({ ...common, type: "handover", ...(r ? { reason: r } : {}) });
    }
    case "await": {
      const fields = Object.fromEntries(Object.entries(ans.wait ?? { after: "PT1H" }).filter(([k, v]) => v !== undefined && k !== "id" && k !== "type"));
      return one({ ...common, type: "wait", ...fields });
    }
    case "parallel": {
      const withNode = (s: AgentSpec, n: SpecNode): AgentSpec => ({ ...s, spec: { ...s.spec, graph: { ...s.spec.graph, nodes: [...s.spec.graph.nodes, n] } } });
      let taken = withNode(spec, { id, type: "parallel" });
      // New side-by-side steps made here (G-flow-10), each from its own kind's defaults.
      const made: SpecNode[] = [];
      for (const b of ans.newBranches ?? []) {
        if (!(b.label.en.trim() || b.label.ar.trim())) continue;
        const n = buildStep(taken, b.kind, { label: b.label }, null).nodes[0] as SpecNode;
        made.push(n);
        taken = withNode(taken, n);
      }
      const joinId = uniqueId(taken, `${id}_wait`);
      const branches = [...(ans.branches ?? []), ...made.map((n) => n.id)];
      return {
        nodes: [
          { ...common, type: "parallel", branches, join: joinId },
          ...made,
          { id: joinId, type: "join", strategy: ans.combine ?? "merge", ...(label ? { label: { en: `${label.en}: wait for all`, ar: `${label.ar}: انتظار الكل` } } : {}) },
        ],
        edges: branches.map((b) => ({ from: b, to: joinId })),
        entry: id,
        exit: joinId,
      };
    }
    default:
      return one({ ...common, type: "end" });
  }
}

/**
 * Adds the new step where it goes: on an arrow the arrow now ends at the new step and the step
 * continues to the arrow's old target; after a step it follows that step; grants the system and
 * declares the knowledge it uses. Returns the new spec, the new step's id and what changed.
 */
export function insertStep(spec: AgentSpec, kind: AddKind, ans: StepAnswers, place: StepPlace): { spec: AgentSpec; id: string; from: string | null; to: string | null } {
  const ends = placeEnds(spec, place);
  const g = spec.spec.graph;
  // A blank agent starts at its end step (G-camp-fin-20): the first step added on its own becomes
  // where it starts, and goes on to the end.
  const startsAtEnd = !place && g.nodes.find((n) => n.id === g.entry)?.type === "end";
  const from = ends.from;
  const to = startsAtEnd ? g.entry : ends.to;
  const built = buildStep(spec, kind, ans, to, from);
  let next: AgentSpec = { ...spec, spec: { ...spec.spec, graph: { ...g, nodes: [...g.nodes, ...built.nodes], edges: [...(g.edges ?? []), ...built.edges], entry: g.nodes.length && !startsAtEnd ? g.entry : built.entry } } };
  // An answer of an AI choice that already has a path: the step goes on that arrow.
  const optionEdge = place && "after" in place && place.when ? editorEdges(spec).find((x) => x.source === place.after && x.when === place.when) : undefined;
  if (place && "edgeId" in place) next = retargetEdge(next, place.edgeId, built.entry);
  else if (optionEdge) next = retargetEdge(next, optionEdge.id, built.entry);
  else if (from) next = { ...next, spec: { ...next.spec, graph: { ...next.spec.graph, edges: [...(next.spec.graph.edges ?? []), { from, to: built.entry, ...(place && "after" in place && place.when ? { when: place.when } : {}) }] } } };
  // A choice already goes on to the old target (the rule's "otherwise", or one arrow per answer); others continue there.
  const continues = to && built.exit && kind !== "choose";
  if (continues) next = { ...next, spec: { ...next.spec, graph: { ...next.spec.graph, edges: [...(next.spec.graph.edges ?? []), { from: built.exit as string, to: to as string }] } } };
  // A step added among a for-each step's steps is done for each item too (ADR-0104).
  const owner = next.spec.graph.nodes.find((n) => n.type === "foreach" && ((to && (n.steps ?? []).includes(to)) || (from && (n.steps ?? []).includes(from))) && !built.nodes.some((b) => b.id === n.id));
  if (owner) {
    const added = built.nodes.map((b) => b.id).filter((b) => !(owner.steps ?? []).includes(b));
    next = { ...next, spec: { ...next.spec, graph: { ...next.spec.graph, nodes: next.spec.graph.nodes.map((n) => (n.id === owner.id ? { ...n, steps: [...(n.steps ?? []), ...added] } : n)) } } };
  }
  for (const n of built.nodes) {
    if (n.type === "tool" && n.tool && n.action) next = ensureGrant(next, n.tool, n.action);
    if (n.type === "retrieval" && n.collection) next = ensureKnowledge(next, n.collection);
  }
  return { spec: next, id: built.entry, from, to };
}

/** The answers of an AI choice and the step each one goes to (null: no path yet). */
export function choicePaths(spec: AgentSpec, id: string): { value: string; label: Text; to: string | null }[] {
  const n = spec.spec.graph.nodes.find((x) => x.id === id);
  if (!n || !routesByOption(n)) return [];
  const edges = (spec.spec.graph.edges ?? []).filter((e) => e.from === id);
  return decisionOptions(n).map((o) => ({ value: o.value, label: o.label, to: edges.find((e) => e.when === o.value)?.to ?? null }));
}

/** The state key a new step writes, as later steps read it. */
export function writtenKey(n: SpecNode): string | null {
  const out = typeof n.output_key === "string" && n.output_key ? n.output_key : null;
  switch (n.type) {
    case "tool":
      return out ?? `${n.id}_result`;
    case "llm":
      return out && out !== "messages" ? out : null;
    case "retrieval":
      return out ?? "context";
    case "subgraph":
      return out ?? "subgraph_result";
    case "human_input":
      return out;
    case "human_approval":
      return `${n.id}_decision`;
    case "decision":
      return out ?? n.id;
    case "wait":
      return out ?? `${n.id}_result`;
    case "calculate":
      return n.values?.[0]?.key ?? null;
    case "foreach":
      return out ?? `${n.id}_results`;
    case "table":
      return out ?? `${n.id}_table`;
    default:
      return null;
  }
}

/** Whether a step reads a state key (in its arguments, prompt, query, inputs or subject). */
export function reads(n: SpecNode, key: string): boolean {
  const re = new RegExp(`state\\.${key}\\b|\\{${key}\\}`);
  const text = JSON.stringify({ args: n.args, prompt: n.prompt, query: n.query, input: n.input, input_map: n.input_map, event: n.event, at: n.at });
  return re.test(text) || n.subject_key === key || (isObj(n.prompt) && Array.isArray(n.prompt.variables) && (n.prompt.variables as string[]).includes(key));
}

/** One click: let a later AI or Run code step read the new value (the "done" suggestion). */
export function letRead(spec: AgentSpec, nodeId: string, key: string): AgentSpec {
  const nodes = spec.spec.graph.nodes.map((n) => {
    if (n.id !== nodeId) return n;
    if (n.type === "llm" && isObj(n.prompt) && typeof n.prompt.template === "string") {
      const vars = Array.isArray(n.prompt.variables) ? (n.prompt.variables as string[]) : [];
      return { ...n, prompt: { ...n.prompt, template: `${n.prompt.template}\n\n{${key}}`, variables: [...new Set([...vars, key])] } };
    }
    if (n.type === "tool" && n.tool === "codeexec" && isObj(n.args)) {
      const input = isObj(n.args.input) ? n.args.input : {};
      return { ...n, args: { ...n.args, input: { ...input, [key]: template(key) } } };
    }
    return n;
  });
  return { ...spec, spec: { ...spec.spec, graph: { ...spec.spec.graph, nodes } } };
}

// ---------------------------------------------------------------- one "Choose a path", one "Several at once"

/** The fields every kind of step keeps when it changes kind (its name and description). */
function keep(n: SpecNode): Partial<SpecNode> {
  const out: Partial<SpecNode> = {};
  for (const k of ["label", "description", "output_label", "max_iterations", "on_limit"] as const) if (n[k] !== undefined) (out as Record<string, unknown>)[k] = n[k];
  return out;
}

/**
 * "Choose a path" by a rule (router) or by the AI (decision), as one kind of step (board 15).
 * Switching keeps the step's name and the steps it can go to: rule → AI makes one answer per
 * target with its arrow; AI → rule goes to the first target until rules are set.
 */
export function switchChoose(spec: AgentSpec, id: string, to: "rule" | "ai"): AgentSpec {
  const g = spec.spec.graph;
  const n = g.nodes.find((x) => x.id === id);
  if (!n || (to === "ai" && n.type !== "router") || (to === "rule" && n.type !== "decision")) return spec;
  const named = (t: string) => g.nodes.find((x) => x.id === t)?.label;
  if (to === "ai") {
    const targets = [...new Set([...(n.routes ?? []).map((r) => r.to), n.default ?? ""].filter(Boolean))];
    const options = targets.map((t) => ({ value: t, label: { en: named(t)?.en || t, ar: named(t)?.ar || named(t)?.en || t } }));
    const node: SpecNode = { ...defaultNode("decision", id), ...keep(n), ...(options.length ? { options } : {}) };
    const edges = [...(g.edges ?? []), ...(options.length ? targets.map((t) => ({ from: id, to: t, when: t })) : [])];
    return { ...spec, spec: { ...spec.spec, graph: { ...g, nodes: g.nodes.map((x) => (x.id === id ? node : x)), edges } } };
  }
  const out = (g.edges ?? []).filter((e) => e.from === id).map((e) => e.to);
  const node: SpecNode = { id, type: "router", ...keep(n), routes: [], default: out[0] ?? "" };
  return { ...spec, spec: { ...spec.spec, graph: { ...g, nodes: g.nodes.map((x) => (x.id === id ? node : x)), edges: (g.edges ?? []).filter((e) => e.from !== id) } } };
}

/** "Several at once" without its wait step: adds it, after the parallel steps (board 15). */
export function addWaitStep(spec: AgentSpec, id: string): AgentSpec {
  const g = spec.spec.graph;
  const n = g.nodes.find((x) => x.id === id);
  if (!n || n.type !== "parallel" || (n.join && g.nodes.some((x) => x.id === n.join))) return spec;
  const joinId = uniqueId(spec, `${id}_wait`);
  const label = n.label ? { label: { en: `${n.label.en}: wait for all`, ar: `${n.label.ar}: انتظار الكل` } } : {};
  return {
    ...spec,
    spec: {
      ...spec.spec,
      graph: {
        ...g,
        nodes: [...g.nodes.map((x) => (x.id === id ? { ...x, join: joinId } : x)), { id: joinId, type: "join", strategy: "merge", ...label }],
        edges: [...(g.edges ?? []), ...(n.branches ?? []).map((b) => ({ from: b, to: joinId }))],
      },
    },
  };
}

/** The "Several at once" step a wait step belongs to, if any. */
export function waitOwner(spec: AgentSpec, joinId: string): string | null {
  return spec.spec.graph.nodes.find((x) => x.type === "parallel" && x.join === joinId)?.id ?? null;
}
